import { useEffect } from 'react'
import {
  Room,
  RoomEvent,
  Track,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
} from 'livekit-client'

import { clientLogInfo, clientLogWarn } from '@/src/lib/clientLog'
import { collectReceiverSnapshot } from '@/src/lib/webrtcStats'
import {
  CODEC_WATCHDOG_SAMPLE_DELAYS_MS,
  COMPLAINT_STREAK_TO_SEND,
  SCREEN_SHARE_CODEC_TOPIC,
  canSampleForCodecWatchdog,
  complaintAllowed,
  decodeScreenShareCodecMessage,
  encodeScreenShareCodecMessage,
  noteCodecSwitchSettled,
  noteCodecSwitchStarting,
  shouldComplainAboutCodec,
  updateComplaintStreak,
  type ScreenShareReceiverCounters,
} from '@/src/lib/screenShareCodec'

/** Опрос после стартового всплеска: ловит деградацию по ходу звонка. */
const WATCHDOG_STEADY_INTERVAL_MS = 30_000

type WatchedTrack = {
  track: RemoteTrack
  publication: RemoteTrackPublication
  publisherIdentity: string
  timers: Array<ReturnType<typeof setTimeout>>
  steadyTimer: ReturnType<typeof setTimeout> | null
  prev: ScreenShareReceiverCounters | null
  badStreak: number
  sentCount: number
  lastSentAt: number | null
}

export type UseScreenShareCodecWatchdogOptions = {
  room?: Room
  enabled?: boolean
}

/**
 * Следит за декодированием чужой демонстрации и просит VP8 при чёрном экране.
 *
 * История: отказ конкретного H.264-битстрима не виден ни в SDP, ни в `backupCodec`
 * SDK — кодек заявлен и «поддерживается», а кадры не разбираются. Хук сэмплит
 * `getStats` на 1/2/3 секунде после подписки и шлёт жалобу публикатору через
 * data channel, ответ и заморозку пина обрабатывает по сообщениям протокола.
 *
 * @param options.room - комната livekit, пока undefined хук неактивен
 * @param options.enabled - выключатель, по умолчанию включён
 */
export function useScreenShareCodecWatchdog({
  room,
  enabled = true,
}: UseScreenShareCodecWatchdogOptions): void {
  useEffect(() => {
    if (!room || !enabled) {
      return
    }

    const watched = new Map<string, WatchedTrack>()
    let disposed = false

    /**
     * Отправляет жалобу публикатору с лимитами: пауза и максимум на трек.
     */
    const sendComplaint = async (
      entry: WatchedTrack,
      trackSid: string,
      counters: ScreenShareReceiverCounters,
    ): Promise<void> => {
      const now = Date.now()

      if (!complaintAllowed(entry.sentCount, entry.lastSentAt, now)) {
        return
      }

      try {
        await room.localParticipant.publishData(
          encodeScreenShareCodecMessage({
            type: 'SCREEN_SHARE_CODEC_COMPLAINT',
            trackSid,
            publisherIdentity: entry.publisherIdentity,
            framesReceived: counters.framesReceived,
            framesDecoded: counters.framesDecoded,
            keyFramesDecoded: counters.keyFramesDecoded,
          }),
          {
            reliable: true,
            topic: SCREEN_SHARE_CODEC_TOPIC,
            destinationIdentities: [entry.publisherIdentity],
          },
        )

        entry.sentCount += 1
        entry.lastSentAt = now

        clientLogInfo('screen-share-codec-complaint-sent', {
          room: room.name,
          trackSid,
          publisher: entry.publisherIdentity,
          viewer: room.localParticipant.identity,
          framesReceived: counters.framesReceived,
          framesDecoded: counters.framesDecoded,
          keyFramesDecoded: counters.keyFramesDecoded,
          attempt: entry.sentCount,
        })
      } catch (error) {
        clientLogWarn('screen-share-codec-complaint-failed', {
          room: room.name,
          trackSid,
          error: error instanceof Error ? error.message : String(error),
        })
      }
    }

    /**
     * Делает один сэмпл: снимок, фильтры честности и решение по дельте.
     */
    const takeSample = async (trackSid: string): Promise<void> => {
      const entry = watched.get(trackSid)

      if (!entry || disposed) {
        return
      }

      try {
        const snapshot = await collectReceiverSnapshot(entry.track, entry.publisherIdentity, trackSid)
        const live = watched.get(trackSid)

        // Пока ждали отчёт, трек могли отписать — проверять нечего.
        if (!snapshot || disposed || !live) {
          return
        }

        const counters: ScreenShareReceiverCounters = {
          packetsReceived: snapshot.packetsReceived,
          framesReceived: snapshot.framesReceived,
          framesDecoded: snapshot.framesDecoded,
          keyFramesDecoded: snapshot.keyFramesDecoded,
          pliCount: snapshot.pliCount,
        }
        const mime = snapshot.mimeType?.toLowerCase() ?? ''

        if (
          !canSampleForCodecWatchdog({
            tabVisible: document.visibilityState !== 'hidden',
            subscribed: entry.publication.isSubscribed,
            muted: entry.track.isMuted,
            isVp8: mime === 'video/vp8',
          })
        ) {
          return
        }

        if (entry.prev) {
          const bad = shouldComplainAboutCodec(entry.prev, counters)

          entry.badStreak = updateComplaintStreak(entry.badStreak, bad)

          // Первая плохая дельта жалобу не вызывает: на второй секунде ключевой
          // кадр ещё может опаздывать, серия из двух подряд — уже отказ.
          if (entry.badStreak >= COMPLAINT_STREAK_TO_SEND) {
            const before = entry.sentCount

            await sendComplaint(entry, trackSid, counters)

            if (entry.sentCount > before) {
              entry.badStreak = 0
            }
          }
        }

        entry.prev = counters
      } catch {
        // Снимок мог не собраться между таймером и опросом — ждём следующий
      }
    }

    /**
     * Ставит повторяющийся опрос трека после стартового всплеска.
     */
    const scheduleSteady = (trackSid: string): void => {
      const entry = watched.get(trackSid)

      if (!entry || disposed) {
        return
      }

      entry.steadyTimer = setTimeout(() => {
        void takeSample(trackSid).then(() => {
          const live = watched.get(trackSid)

          if (live && !disposed) {
            scheduleSteady(trackSid)
          }
        })
      }, WATCHDOG_STEADY_INTERVAL_MS)
    }

    /**
     * Начинает наблюдение: стартовый всплеск и затем редкий опрос.
     */
    const startWatch = (
      track: RemoteTrack,
      publication: RemoteTrackPublication,
      publisherIdentity: string,
    ): void => {
      const trackSid = publication.trackSid

      if (watched.has(trackSid)) {
        return
      }

      const entry: WatchedTrack = {
        track,
        publication,
        publisherIdentity,
        timers: [],
        steadyTimer: null,
        prev: null,
        badStreak: 0,
        sentCount: 0,
        lastSentAt: null,
      }

      watched.set(trackSid, entry)

      CODEC_WATCHDOG_SAMPLE_DELAYS_MS.forEach((delay) => {
        entry.timers.push(
          setTimeout(() => {
            void takeSample(trackSid)
          }, delay),
        )
      })

      scheduleSteady(trackSid)
    }

    /**
     * Останавливает наблюдение и чистит таймеры трека.
     */
    const stopWatch = (trackSid: string): void => {
      const entry = watched.get(trackSid)

      if (!entry) {
        return
      }

      watched.delete(trackSid)
      entry.timers.forEach((timer) => clearTimeout(timer))
      entry.timers = []

      if (entry.steadyTimer !== null) {
        clearTimeout(entry.steadyTimer)
        entry.steadyTimer = null
      }
    }

    const handleTrackSubscribed = (
      track: RemoteTrack,
      publication: RemoteTrackPublication,
      participant: RemoteParticipant,
    ): void => {
      if (publication.source !== Track.Source.ScreenShare || track.kind !== Track.Kind.Video) {
        return
      }

      startWatch(track, publication, participant.identity)
    }

    const handleTrackUnsubscribed = (
      _track: RemoteTrack,
      publication: RemoteTrackPublication,
    ): void => {
      if (publication.source !== Track.Source.ScreenShare) {
        return
      }

      stopWatch(publication.trackSid)
    }

    /**
     * Обрабатывает итог перехода: заморозка пина и сброс жалоб по старому треку.
     *
     * Новый sid придёт обычной подпиской и заведёт свежее наблюдение —
     * детектор для него включится заново, но не раньше ~3 с после подписки.
     */
    const handleDataReceived = (payload: Uint8Array): void => {
      const message = decodeScreenShareCodecMessage(payload)

      if (!message) {
        return
      }

      if (message.type === 'SCREEN_SHARE_CODEC_SWITCHING') {
        noteCodecSwitchStarting(message.publisherIdentity)

        return
      }

      if (message.type === 'SCREEN_SHARE_CODEC_SWITCHED') {
        noteCodecSwitchSettled(message.publisherIdentity)
        stopWatch(message.oldTrackSid)

        clientLogInfo('screen-share-codec-ack-received', {
          room: room.name,
          oldTrackSid: message.oldTrackSid,
          newTrackSid: message.newTrackSid,
          publisher: message.publisherIdentity,
          codecFrom: message.codecFrom,
          codecTo: message.codecTo,
          ok: message.ok,
        })
      }
    }

    // Хук может смонтироваться позже подписки: перебираем то, что уже смотрим.
    room.remoteParticipants.forEach((participant) => {
      participant.trackPublications.forEach((publication) => {
        if (publication.source !== Track.Source.ScreenShare) {
          return
        }

        const videoTrack = publication.videoTrack

        if (
          publication.isSubscribed &&
          videoTrack &&
          'receiver' in videoTrack &&
          videoTrack.kind === Track.Kind.Video
        ) {
          startWatch(videoTrack, publication, participant.identity)
        }
      })
    })

    room
      .on(RoomEvent.TrackSubscribed, handleTrackSubscribed)
      .on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed)
      .on(RoomEvent.DataReceived, handleDataReceived)

    return () => {
      disposed = true

      room
        .off(RoomEvent.TrackSubscribed, handleTrackSubscribed)
        .off(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed)
        .off(RoomEvent.DataReceived, handleDataReceived)

      watched.forEach((entry) => {
        entry.timers.forEach((timer) => clearTimeout(timer))

        if (entry.steadyTimer !== null) {
          clearTimeout(entry.steadyTimer)
        }
      })

      watched.clear()
    }
  }, [room, enabled])
}
